import React from 'react';

import parseUrl from 'parse-url';

import { getLinkTypeDisplayValue } from './helpers';
import { URL_NORMALIZE_OPTIONS } from './LinkRenderer.constants';
import { LinkRendererView } from './LinkRendererView';

import type { LinkRendererProperties } from './LinkRenderer.types';

export const LinkRenderer = <T,>({
  link,
  isDeletable,
  isDisabled,
  onDelete,
  onTypeChange,
  dataTest = 'link_renderer',
  linkTypes,
  microLinkData,
  isMicroLinkPending = false,
}: LinkRendererProperties<T>) => {
  const linkTypeDisplayValue = React.useMemo(
    () => getLinkTypeDisplayValue(link, linkTypes),
    [link, linkTypes],
  );

  const urlNormalised = React.useMemo(() => {
    if (!link.url) return '';
    try {
      return parseUrl(link.url, URL_NORMALIZE_OPTIONS).resource;
    } catch {
      return link.url;
    }
  }, [link]);

  const handleDelete = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onDelete?.(link.id);
    },
    [link.id, onDelete],
  );

  const handleTypeChange = React.useCallback(
    (typeValue: string) => {
      onTypeChange?.(link.id, typeValue);
    },
    [link.id, onTypeChange],
  );

  return (
    <LinkRendererView
      url={link.url}
      isPending={isMicroLinkPending}
      isDisabled={isDisabled}
      isDeletable={isDeletable}
      screenshotUrl={microLinkData?.data.screenshot?.url}
      title={microLinkData?.data.title ?? undefined}
      description={microLinkData?.data.description}
      urlNormalised={urlNormalised}
      linkTypeDisplayValue={linkTypeDisplayValue}
      linkTypeChoices={linkTypes?.url_type}
      onTypeChange={onTypeChange ? handleTypeChange : undefined}
      onDelete={handleDelete}
      dataTest={dataTest}
    />
  );
};
